import { useEffect, useState } from 'react';
import { api, type CallbackRow } from '../api.js';
import { faNum } from '../labels.js';

/**
 * Everyone waiting for a call back.
 *
 * Built for the opposite job to the conversation queue: not somebody watching
 * chats arrive, but somebody who opens this a few times a day, works down the
 * list and closes it. So every row carries what is needed to pick up a phone —
 * who, what about, which app, which number — and the only action is "done".
 */

const CHANNEL_FA: Record<CallbackRow['channel'], string> = {
  whatsapp: 'واتساپ',
  telegram: 'تلگرام',
  sms: 'پیامک',
  call: 'تماس تلفنی',
};

/*
 * Colour carries the same meaning as everywhere else in the panel: green is
 * live, blue is the thing you click. WhatsApp and Telegram get a link, so they
 * are blue; a call and an SMS are something you do on a handset.
 */
const CHANNEL_TONE: Record<CallbackRow['channel'], string> = {
  whatsapp: 'bg-ok/10 text-ok-strong',
  telegram: 'bg-brand/10 text-brand',
  sms: 'bg-ink/[0.06] text-ink/70',
  call: 'bg-warn/10 text-warn',
};

const REASON_FA: Record<string, string> = {
  explicit_request: 'خودش آدم خواست',
  order_dispute: 'مشکل سفارش',
  dissatisfaction: 'نارضایتی',
  low_confidence: 'ربات قول پیگیری داد',
  empty_results: 'چیزی پیدا نشد',
  model_unavailable: 'ربات در دسترس نبود',
  medical_sign: 'نشانه پزشکی',
};

function waitedFor(iso: string): { text: string; late: boolean } {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return { text: `${faNum(String(mins))} دقیقه پیش`, late: false };
  const hours = Math.round(mins / 60);
  // Past a working day, a callback request has stopped being a queue and
  // started being a complaint.
  if (hours < 24) return { text: `${faNum(String(hours))} ساعت پیش`, late: hours >= 6 };
  const days = Math.round(hours / 24);
  return { text: `${faNum(String(days))} روز پیش`, late: true };
}

function personName(row: CallbackRow): string {
  return (
    row.displayName?.trim() ||
    (row.visitorNumber ? `بازدیدکننده ${faNum(String(row.visitorNumber))}` : 'بازدیدکننده')
  );
}

/**
 * A number the operator can act on rather than retype.
 *
 * WhatsApp and Telegram open where the conversation will happen; a phone number
 * dials. Stored as `09…` because that is what a person reads, converted to the
 * international form only where the link demands it.
 */
function reachLink(row: CallbackRow): string | null {
  const digits = (row.contactValue ?? '').replace(/\D/g, '');
  if (!digits) return null;
  const intl = `98${digits.replace(/^0/, '')}`;
  if (row.channel === 'whatsapp') return `https://wa.me/${intl}`;
  if (row.channel === 'telegram') return `https://t.me/+${intl}`;
  return `tel:+${intl}`;
}

export function Callbacks({
  inboxId,
  onOpenConversation,
}: {
  inboxId?: string | undefined;
  onOpenConversation?: ((sessionId: string) => void) | undefined;
}) {
  const [rows, setRows] = useState<CallbackRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [includeDone, setIncludeDone] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    const params: Record<string, string> = {};
    if (inboxId) params['inboxId'] = inboxId;
    if (includeDone) params['includeDone'] = 'true';
    const res = await api.callbacks(params);
    setRows(res.callbacks);
    setLoaded(true);
  }

  useEffect(() => {
    void load();
    const tick = setInterval(() => void load(), 30_000);
    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxId, includeDone]);

  async function markDone(id: string) {
    setBusy(id);
    try {
      await api.callbackDone(id);
      await load();
    } catch {
      // Somebody else got to it first; reloading shows the truth.
      await load();
    } finally {
      setBusy(null);
    }
  }

  const open = rows.filter((r) => r.status === 'queued');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink/60">
          کسانی که خواستند بهشان خبر بدهیم. ربات مشکل و راه ارتباطی را گرفته؛
          کافی است زنگ بزنی و «انجام شد» را بزنی.
        </p>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-ink/70">
          <input
            type="checkbox"
            checked={includeDone}
            onChange={(e) => setIncludeDone(e.target.checked)}
            className="h-4 w-4 accent-brand"
          />
          انجام‌شده‌ها را هم نشان بده
        </label>
      </div>

      {loaded && open.length === 0 && !includeDone && (
        <div className="rounded-xl border border-ink/10 bg-white p-8 text-center">
          <p className="font-medium text-ink-strong">کسی منتظر تماس نیست</p>
          <p className="mt-1 text-sm text-ink/55">
            هر وقت ربات نتواند کاری را تمام کند، راه ارتباطی می‌گیرد و اینجا می‌آید.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {rows.map((row) => {
          const waited = waitedFor(row.requestedAt);
          const link = reachLink(row);
          const done = row.status !== 'queued';
          return (
            <div
              key={row.id}
              className={`rounded-xl border bg-white p-4 ${
                done ? 'border-ink/10 opacity-60' : 'border-ink/10'
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-ink-strong">{personName(row)}</span>
                    {row.identityVerified && (
                      <span className="rounded-full bg-ok/10 px-2 py-0.5 text-2xs font-medium text-ok-strong">
                        تأییدشده
                      </span>
                    )}
                    <span className="rounded-full bg-ink/[0.06] px-2 py-0.5 text-2xs text-ink/70">
                      {REASON_FA[row.reason] ?? row.reason}
                    </span>
                    <span
                      className={`text-2xs ${waited.late && !done ? 'text-warn' : 'text-ink/45'}`}
                    >
                      {waited.text}
                    </span>
                  </div>

                  {row.problem && (
                    <p className="mt-2 line-clamp-2 text-sm text-ink/75">«{row.problem}»</p>
                  )}
                </div>

                <div className="flex shrink-0 flex-col items-stretch gap-2 sm:items-end">
                  <div className="flex items-center gap-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-2xs font-medium ${
                        CHANNEL_TONE[row.channel]
                      }`}
                    >
                      {CHANNEL_FA[row.channel]}
                    </span>
                    {link ? (
                      <a
                        href={link}
                        target="_blank"
                        rel="noreferrer"
                        className="font-mono text-sm text-brand hover:underline"
                        dir="ltr"
                      >
                        {faNum(row.contactValue ?? '')}
                      </a>
                    ) : (
                      <span className="text-sm text-ink/45">شماره‌ای ثبت نشد</span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {onOpenConversation && (
                      <button
                        type="button"
                        onClick={() => onOpenConversation(row.sessionId)}
                        className="rounded-lg border border-ink/15 px-3 py-1.5 text-xs text-ink/70 hover:bg-ink/[0.04]"
                      >
                        گفتگو
                      </button>
                    )}
                    {done ? (
                      <span className="text-xs text-ok-strong">انجام شد ✓</span>
                    ) : (
                      <button
                        type="button"
                        disabled={busy === row.id}
                        onClick={() => void markDone(row.id)}
                        className="rounded-lg bg-ink-strong px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                      >
                        {busy === row.id ? '…' : 'انجام شد'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
